import { EditorState, StateField } from '@codemirror/state';
import { Decoration, EditorView, drawSelection, highlightActiveLine, highlightActiveLineGutter, keymap, lineNumbers } from '@codemirror/view';
import { autocompletion, completionKeymap } from '@codemirror/autocomplete';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';

const voices = [
  ['Achernar','Soft · higher pitch'],['Achird','Friendly · lower middle'],['Algenib','Gravelly · lower'],['Algieba','Smooth · lower'],['Alnilam','Firm · lower middle'],['Aoede','Breezy · middle'],['Autonoe','Bright · middle'],['Callirrhoe','Easy-going · middle'],['Charon','Informative · lower'],['Despina','Smooth · middle'],['Enceladus','Breathy · lower'],['Erinome','Clear · middle'],['Fenrir','Excitable · lower middle'],['Gacrux','Mature · middle'],['Iapetus','Clear · lower middle'],['Kore','Firm · middle'],['Laomedeia','Upbeat · higher'],['Leda','Youthful · higher'],['Orus','Firm · lower middle'],['Puck','Upbeat · middle'],['Pulcherrima','Forward · middle'],['Rasalgethi','Informative · middle'],['Sadachbia','Lively · lower'],['Sadaltager','Knowledgeable · middle'],['Schedar','Even · lower middle'],['Sulafat','Warm · middle'],['Umbriel','Easy-going · lower middle'],['Vindemiatrix','Gentle · middle'],['Zephyr','Bright · higher'],['Zubenelgenubi','Casual · lower middle']
];
const cues = ['angry','caution','determination','excited','fearful','gentle','laughing','pensive','sad','shouting','suspicion','urgency','whispers'];
const defaultScript = `[shouting] Halt, traveler! The northern pass is sealed by order of the council.
[determination] I carry a message for the elder. Step aside, or I will force my way through.
[caution] No one passes. [pensive] The elder is... he's no longer receiving visitors.
[suspicion] What do you mean? We don't have time for games.
It's too late. [whispers] The shadow... it reached him first. [urgency] You need to leave. [shouting] Now.`;
const multiScript = `Speaker 1: [shouting] Halt, traveler! The northern pass is sealed by order of the council.
Speaker 2: [determination] I carry a message for the elder. Step aside, or I will force my way through.
Speaker 1: [caution] No one passes. [pensive] The elder is... he's no longer receiving visitors.
Speaker 2: [suspicion] What do you mean? We don't have time for games.
Speaker 1: It's too late. [whispers] The shadow... it reached him first. [urgency] You need to leave. [shouting] Now.`;

function decorations(state) {
  const ranges = [];
  const text = state.doc.toString();
  const add = (regexp, className, classFor) => {
    regexp.lastIndex = 0;
    let match;
    while ((match = regexp.exec(text))) {
      const value = match[1] || match[0];
      const offset = match[0].indexOf(value);
      ranges.push(Decoration.mark({ class: classFor ? classFor(value) : className }).range(match.index + offset, match.index + offset + value.length));
      if (match[0].length === 0) regexp.lastIndex += 1;
    }
  };
  add(/^(Speaker\s+\d+|[A-Z][\w .'-]{0,24})(?=:)/gm, '', value => `oa-speaker-${Math.abs([...value].reduce((sum, char) => sum + char.charCodeAt(0), 0)) % 6}`);
  add(/\[[^\]\n]{2,32}\]/g, 'oa-cue');
  const voicePattern = new RegExp(`\\b(${voices.map(item => item[0]).join('|')})\\b`, 'g');
  add(voicePattern, 'oa-voice');
  add(/^(#{1,3}\s[^\n]+)/gm, 'oa-heading');
  return Decoration.set(ranges.sort((a, b) => a.from - b.from), true);
}

const syntaxField = StateField.define({
  create: decorations,
  update(value, transaction) { return transaction.docChanged ? decorations(transaction.state) : value.map(transaction.changes); },
  provide: field => EditorView.decorations.from(field)
});

function completions(context) {
  const bracket = context.matchBefore(/\[[a-z-]*$/i);
  if (bracket) return { from: bracket.from, options: cues.map(cue => ({ label: `[${cue}]`, type: 'keyword', detail: 'performance cue' })) };
  const word = context.matchBefore(/[\w'-]*$/);
  if (!word || (!context.explicit && word.from === word.to)) return null;
  return {
    from: word.from,
    options: [
      { label: 'Speaker 1: ', type: 'variable', detail: 'character line' },
      { label: 'Speaker 2: ', type: 'variable', detail: 'character line' },
      ...voices.map(item => ({ label: item[0], type: 'constant', detail: item[1] }))
    ]
  };
}

function authHeaders() {
  const user = window.currentUser || window.userData || {};
  const key = user.secret || user.api_key || (user.user && (user.user.secret || user.user.api_key)) || '';
  const headers = { 'Content-Type': 'application/json' };
  if (key) { headers.Authorization = `Bearer ${key}`; headers['X-API-Key'] = key; }
  return headers;
}

function requireLogin() {
  if (window.NetwrckTools && typeof window.NetwrckTools.requireLogin === 'function') return window.NetwrckTools.requireLogin();
  if (typeof window.showSignupDialog === 'function') return window.showSignupDialog(null, '/tools/google-tts');
  window.location.href = '/login?next=' + encodeURIComponent('/tools/google-tts');
}

document.addEventListener('DOMContentLoaded', function () {
  const shell = document.getElementById('google-tts-shell');
  const mount = document.getElementById('tts-editor');
  if (!shell || !mount) return;
  let mode = 'single';
  const editor = new EditorView({
    state: EditorState.create({
      doc: defaultScript,
      extensions: [lineNumbers(), highlightActiveLineGutter(), history(), drawSelection(), highlightActiveLine(), EditorView.lineWrapping, syntaxField, autocompletion({ override: [completions], activateOnTyping: true }), keymap.of([...defaultKeymap, ...historyKeymap, ...completionKeymap, indentWithTab])]
    }),
    parent: mount
  });
  document.querySelectorAll('.tts-voice').forEach(function (select, index) {
    voices.forEach(function (item) { const option = document.createElement('option'); option.value = item[0]; option.textContent = `${item[0]} — ${item[1]}`; select.appendChild(option); });
    select.value = index === 0 ? 'Zephyr' : 'Puck';
  });
  const setMode = function (next) {
    if (mode === next) return;
    const current = editor.state.doc.toString();
    mode = next;
    if (mode === 'multi' && document.getElementById('tts-voice-1').value === 'Zephyr') document.getElementById('tts-voice-1').value = 'Fenrir';
    shell.classList.toggle('oa-multi', mode === 'multi'); shell.classList.toggle('oa-single', mode === 'single');
    document.getElementById('tts-single').classList.toggle('active', mode === 'single'); document.getElementById('tts-multi').classList.toggle('active', mode === 'multi');
    const converted = mode === 'multi' ? (current.includes('Speaker 1:') ? current : multiScript) : current.replace(/^Speaker\s+\d+:\s*/gm, '');
    editor.dispatch({ changes: { from: 0, to: editor.state.doc.length, insert: converted } });
  };
  document.getElementById('tts-single').addEventListener('click', () => setMode('single'));
  document.getElementById('tts-multi').addEventListener('click', () => setMode('multi'));
  document.getElementById('tts-cues').addEventListener('click', function (event) {
    const cue = event.target && event.target.dataset && event.target.dataset.cue;
    if (!cue) return;
    const selection = editor.state.selection.main;
    editor.dispatch({ changes: { from: selection.from, to: selection.to, insert: `${cue} ` }, selection: { anchor: selection.from + cue.length + 1 } });
    editor.focus();
  });
  const value = id => document.getElementById(id).value.trim();
  const buildInput = function () {
    const transcript = editor.state.doc.toString().trim();
    const profiles = mode === 'multi' ? `For Speaker 1: ${value('tts-profile-1')}\nFor Speaker 2: ${value('tts-profile-2')}` : value('tts-profile-1');
    const notes = mode === 'multi' ? `For Speaker 1: Style: ${value('tts-style-1')}. Pace: ${value('tts-pace-1')}. Accent: ${value('tts-accent-1')}.\nFor Speaker 2: Style: ${value('tts-style-2')}. Pace: ${value('tts-pace-2')}. Accent: ${value('tts-accent-2')}.` : `Style: ${value('tts-style-1')}. Pace: ${value('tts-pace-1')}. Accent: ${value('tts-accent-1')}.`;
    return `Read the following transcript based on the audio profile and director's note.\n\n# Audio Profile\n${profiles}\n\n# Director's note\n${notes}\n\n## Scene:\n${value('tts-scene')}\n\n## Sample Context:\n${value('tts-context')}\n\n## Transcript:\n${transcript}`;
  };
  const button = document.getElementById('tts-generate');
  button.addEventListener('click', async function () {
    const status = document.getElementById('tts-status');
    const outputState = document.getElementById('tts-output-state');
    button.disabled = true; button.textContent = 'Directing performance…'; status.className = 'oa-status'; status.textContent = 'Generating speech with Gemini Flash TTS…'; outputState.textContent = 'RENDERING';
    try {
      const speakerVoices = mode === 'multi' ? [{ speaker: 'Speaker 1', voice: value('tts-voice-1') }, { speaker: 'Speaker 2', voice: value('tts-voice-2') }] : [];
      const response = await fetch('/api/google-tts', { method: 'POST', credentials: 'same-origin', headers: authHeaders(), body: JSON.stringify({ input: buildInput(), voice: value('tts-voice-1'), speaker_voices: speakerVoices, language: 'en-US', temperature: 1, auto_emotion: document.getElementById('tts-auto-emotion').checked, response_format: 'wav' }) });
      const data = await response.json().catch(() => ({}));
      if (response.status === 401) { requireLogin(); throw new Error('Sign in to generate speech.'); }
      if (!response.ok) throw new Error(data.error || 'Speech generation failed.');
      const source = data.audio_url || `data:audio/wav;base64,${data.audio_base64}`;
      const audio = document.getElementById('tts-audio'); audio.src = source; audio.hidden = false;
      document.getElementById('tts-output-empty').hidden = true;
      const download = document.getElementById('tts-download'); download.href = source; download.classList.add('show');
      document.getElementById('tts-meta').innerHTML = `<span class="oa-pill">${data.input_tokens || 0} text tokens</span><span class="oa-pill">${data.output_tokens || 0} audio tokens</span><span class="oa-pill">${data.credits_charged || 0} credit${data.credits_charged === 1 ? '' : 's'}</span>`;
      status.className = 'oa-status success'; status.textContent = 'Performance ready.'; outputState.textContent = 'READY';
    } catch (error) { status.className = 'oa-status error'; status.textContent = error.message || 'Speech generation failed.'; outputState.textContent = 'FAILED'; }
    finally { button.disabled = false; button.textContent = 'Generate speech'; }
  });
});
